/**
 * Created by Simon Zhao on 9/7/2022.
 * Description:
 * NoteDepositTrigger Component
 * ------ maintenance history ------
 */
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { DiagramConditionModel } from '../../../../models/workflow-condition.model';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';
import { smartSelector } from '../../../../redux';
import { AutomationMappingItem } from '../../../../tamalelibs/models/automation.model';
import { WorkflowTriggerSource } from '../../../../tamalelibs/models/workflow-config.model';
import { getEntryTypeForWebState } from '../../../../tamalelibs/redux/template.reducer';
import { AdhocService } from '../../../../tamalelibs/services/adhoc.service';
import { SingleSelectDropdownFilterConfig } from '../../../../widgets/single-select-dropdown-filter/single-select-dropdown-filter.model';
import { NoteTriggerFilterDialogOptions, NoteTriggerFilterDialogResult } from './note-deposit-trigger-filter-dialog.model';
import { NoteDepositTiggerFilterDialogService } from './note-deposit-trigger-filter-dialog.service';
import { NoteDepositTriggerMetaDialogOptions, NoteDepositTriggerMetaDialogResult } from './note-deposit-trigger-meta-dialog.model';
import { NoteDepositTriggerMetaDialogService } from './note-deposit-trigger-meta-dialog.service';

@Component({
    selector: 'tam-note-deposit-trigger',
    templateUrl: './note-deposit-trigger.component.html',
    styleUrls: ['./note-deposit-trigger.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NoteDepositTriggerComponent implements OnInit, OnDestroy {
    //#region public properties
    @Input()
    triggerSource: WorkflowTriggerSource;

    @Input()
    noteDepositEntityTypeConfig: SingleSelectDropdownFilterConfig;

    existingTriggerFilters: Array<DiagramConditionModel> = [];

    /**
     * the amount of criterias.
     */
    filterNum = 0;

    /**
     * the amount of metadata mapping items.
     */
    metaNum = 0;

    selectedNoteTypeFields = [];

    selectedEntityTypeName: string = StringLiteralsPipe.translate('workflow.select_a_note_type');
    //#endregion

    //#region private properties

    /**
    * the subscription array that would be unsubscribed on destroying.
    */
    private _destroySubscriptions: Array<Subscription> = [];

    //#endregion

    constructor(private _changeDetectorRef: ChangeDetectorRef,
        private _store: Store<any>, private _adhocService: AdhocService,
        private _filterDialogService: NoteDepositTiggerFilterDialogService,
        private _metaDialogService: NoteDepositTriggerMetaDialogService) { }

    //#region public methods

    ngOnInit() {
        const relativeSelectorFunc = (selectid, selector) => {
            return (state) => {
                return selector(smartSelector(selectid, 'entryTemplateReducer')(state));
            };
        };
        this._destroySubscriptions.push(
            this._store.select(relativeSelectorFunc('', getEntryTypeForWebState)).subscribe(res => {
                if (res && res.entryTypes && res.entryTypes.length > 0) {
                    res.entryTypes.forEach(item => {
                        this.noteDepositEntityTypeConfig.data.push({
                            id: item.id,
                            name: item.name,
                            selected: false,
                        });
                    });
                }

                this._loadSource();
            }),
            this.noteDepositEntityTypeConfig.onChangeValue$.subscribe(data => {
                if (data) {
                    this._reset();
                    this._onNoteTypeChange(data.id, data.name);
                }
            }),
            this._filterDialogService.dialogClose$.subscribe((dialogResult: NoteTriggerFilterDialogResult) => {
                if (dialogResult.isConfirmed) {
                    this.triggerSource.condition = dialogResult.conditionResult;
                    this.existingTriggerFilters = dialogResult.filterResult;
                    this._onTriggerFilterChange(dialogResult.filterResult);
                }
            }),
            this._metaDialogService.dialogClose$.subscribe((dialogResult: NoteDepositTriggerMetaDialogResult) => {
                if (dialogResult.isConfirmed) {
                    this.triggerSource.mapping = dialogResult.mappingResult;
                    this._onTriggerMetaChange(dialogResult.mappingResult);
                }
            })
        );
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    onSelectEntityTypeClick(e) {
        this.noteDepositEntityTypeConfig.open$.next(e.target);
    }

    onFilterCriteriaClick(e) {
        if (this._isNoteTypeSelected()) {
            const dialogOptions = new NoteTriggerFilterDialogOptions();
            dialogOptions.fields = this.selectedNoteTypeFields;
            if (this.existingTriggerFilters) {
                dialogOptions.filters = this.existingTriggerFilters;
            }
            if (this.triggerSource && this.triggerSource.condition) {
                dialogOptions.condition = this.triggerSource.condition;
            }
            this._filterDialogService.dialogOpen$.next(dialogOptions);
        }
    }

    onMetaMappingClick(e) {
        if (this._isNoteTypeSelected()) {
            const dialogOptions = new NoteDepositTriggerMetaDialogOptions();
            dialogOptions.noteTypeAdhocFields = this.selectedNoteTypeFields;
            if (this.triggerSource && this.triggerSource.mapping && this.triggerSource.mapping.length > 0) {
                dialogOptions.isUpdate = true;
                dialogOptions.existingMapping = this.triggerSource.mapping;
            }
            this._metaDialogService.dialogOpen$.next(dialogOptions);
        }
    }
    //#endregion

    //#region private methods
    private _loadSource() {
        if (this.triggerSource) {
            if (this.triggerSource.objectTypeID && this.noteDepositEntityTypeConfig.data.length > 0) {
                const noteType = this.noteDepositEntityTypeConfig.data.find(nt => nt.id === this.triggerSource.objectTypeID);
                if (noteType) {
                    this._onNoteTypeChange(noteType.id, noteType.name);
                }
            }
            if (this.triggerSource.condition) {
                this.existingTriggerFilters = DiagramConditionModel.import(this.triggerSource.condition);
                this._onTriggerFilterChange(this.existingTriggerFilters);
            }
            if (this.triggerSource.mapping) {
                this._onTriggerMetaChange(this.triggerSource.mapping);
            }
        }
    }

    private _onNoteTypeChange(typeId: string, typeName: string) {
        this.selectedEntityTypeName = typeName;
        this.triggerSource.objectTypeID = typeId;
        this.triggerSource.objectTypeName = typeName;
        this._adhocService.getEntryAdhocsByEntryType(typeId).subscribe(
            adhocFields => {
                this.selectedNoteTypeFields = adhocFields;
            }
        );
    }

    private _onTriggerFilterChange(filters: Array<DiagramConditionModel>) {
        this.filterNum = filters.length;
    }

    private _onTriggerMetaChange(mappings: Array<AutomationMappingItem>) {
        this.metaNum = mappings.length;
    }

    private _isNoteTypeSelected() {
        return this.selectedEntityTypeName !== StringLiteralsPipe.translate('workflow.select_a_note_type');
    }

    private _reset() {
        this.filterNum = this.metaNum = 0;
        this.selectedEntityTypeName = StringLiteralsPipe.translate('workflow.select_a_note_type');
        this.selectedNoteTypeFields = [];
        this.triggerSource.condition = '';
        this.triggerSource.mapping = [];
        this.triggerSource.objectTypeID = '';
        this.triggerSource.objectTypeName = '';
        this.existingTriggerFilters = [];
    }
    //#endregion
}
